const clamp = (value: number, min = 0, max = 100) => Math.max(min, Math.min(max, value));

export type TeamStrengthState =
  | "unformed"
  | "urgent"
  | "formed"
  | "solid"
  | "strong";

export const getTeamStrengthStatus = (teamOvr?: number) => {
  if (teamOvr === undefined) {
    return { score: undefined, state: undefined };
  }

  const state: TeamStrengthState =
    teamOvr < 40
      ? "unformed"
      : teamOvr < 50
        ? "urgent"
        : teamOvr < 65
          ? "formed"
          : teamOvr < 80
            ? "solid"
            : "strong";

  // The color band is deliberately categorical rather than the underlying
  // rating. Players receive a useful verdict without exposing the raw model.
  const scoreByState: Record<TeamStrengthState, number> = {
    unformed: 10,
    urgent: 30,
    formed: 55,
    solid: 75,
    strong: 95,
  };

  return { score: scoreByState[state], state };
};

export const getHealthScore = (injuredPlayers: number) =>
  injuredPlayers <= 0 ? 100 : clamp(92 - injuredPlayers * 15, 8, 85);

export type RosterState = "complete" | "full" | "short";

export const getRosterStatus = ({
  count,
  max,
  min,
}: {
  count?: number;
  max?: number;
  min?: number;
}) => {
  if (count === undefined || max === undefined || min === undefined) {
    return { missing: undefined, score: undefined, state: undefined };
  }
  if (count < min) {
    return { missing: min - count, score: clamp(35 - (min - count) * 10), state: "short" as const };
  }
  if (count >= max) {
    return { missing: 0, score: 72, state: "full" as const };
  }
  return { missing: 0, score: 92, state: "complete" as const };
};

export type CapSpaceState = "available" | "near" | "over";

export const getCapSpaceStatus = ({
  capSpace,
  payrollLimit,
}: {
  capSpace?: number;
  payrollLimit?: number;
}) => {
  if (capSpace === undefined || payrollLimit === undefined || payrollLimit <= 0) {
    return { score: undefined, state: undefined };
  }

  const ratio = capSpace / payrollLimit;
  if (capSpace < 0) {
    return { score: clamp(35 + ratio * 100, 0, 35), state: "over" as const };
  }
  if (ratio < 0.08) {
    return { score: clamp(48 + ratio * 150, 48, 60), state: "near" as const };
  }
  return { score: clamp(65 + ratio * 180, 65, 100), state: "available" as const };
};

const OWNER_TRUST_SCORES: Record<string, number> = {
  honeymoon: 72,
  excellent: 100,
  good: 86,
  solid: 70,
  shaky: 34,
  hotSeat: 5,
};

export const getOwnerTrustScore = (tier: string) => OWNER_TRUST_SCORES[tier] ?? 50;

export const getSeasonGoalScore = (riskLevel: "clear" | "danger" | "watch") =>
  riskLevel === "danger" ? 12 : riskLevel === "watch" ? 52 : 90;

export const getContractStatusScore = ({
  count,
  urgent,
}: {
  count: number;
  urgent: boolean;
}) => (count <= 0 ? 100 : urgent ? clamp(25 - count * 3, 5, 25) : clamp(68 - count * 5, 32, 63));
